import { FilenameScore, Track } from '../types/Track';
import { normalizePath } from './normalizePath';

/** Matches electron `seratoProcessService` — keep in sync for delete error handling. */
export const SERATO_RUNNING_DELETE_MESSAGE =
  'Please close Serato DJ before removing files.';

export const isSeratoRunningDeleteError = (labeledError: string): boolean => {
  const message = labeledError.includes(': ')
    ? labeledError.slice(labeledError.indexOf(': ') + 2)
    : labeledError;
  return (
    message === SERATO_RUNNING_DELETE_MESSAGE ||
    /close serato dj before/i.test(message)
  );
};

// Helper function to extract filename
export const getFileName = (filePath: string): string => {
  const normalized = normalizePath(filePath);
  return normalized.split('/').pop() || normalized;
};

// Helper function to detect live versions
export const isLiveVersion = (text: string): boolean => {
  if (!text) return false;
  const lowerText = text.toLowerCase();
  return (
    lowerText.includes('live') ||
    lowerText.includes('concert') ||
    lowerText.includes('unplugged') ||
    lowerText.includes('acoustic') ||
    lowerText.includes('session') ||
    lowerText.match(/\b(live at|live in|live from)\b/) !== null
  );
};

// Helper function to detect explicit versions
export const isExplicitVersion = (text: string): boolean => {
  if (!text) return false;
  const lowerText = text.toLowerCase();
  return (
    lowerText.includes('explicit') ||
    lowerText.includes('(e)') ||
    lowerText.includes('[e]') ||
    lowerText.includes('clean') ||
    lowerText.includes('radio edit') ||
    lowerText.includes('censored')
  );
};

// Function to get filename scores for tracks
export const getFilenameScores = (tracks: Track[]): FilenameScore[] => {
  const filenames = tracks.map((t) => getFileName(t.filePath));
  return tracks.map((track, idx) => {
    const filename = filenames[idx];
    let score = 0;
    const fileExtension = filename.split('.').pop()?.toLowerCase() || '';

    switch (fileExtension) {
      case 'flac':
        score += 25;
        break;
      case 'wav':
      case 'aiff':
      case 'alac':
        score += 20;
        break;
      case 'mp3':
        if (track.tags?.bitrate && track.tags.bitrate >= 320) score += 15;
        else if (track.tags?.bitrate && track.tags.bitrate >= 256) score += 12;
        else if (track.tags?.bitrate && track.tags.bitrate >= 192) score += 10;
        else score += 5;
        break;
      case 'aac':
      case 'm4a':
      case 'm4p':
        score += 12;
        break;
      case 'ogg':
        score += 10;
        break;
      default:
        score += 0;
    }

    if (/^\d+\s-\s.+/.test(filename)) score += 10;
    if (/^[^-()[\]]+\s-\s[^-()[\]]+\.[^.]+$/.test(filename)) score += 8;

    const specialChars = (filename.match(/[^a-zA-Z0-9\s.-]/g) || []).length;
    score -= specialChars * 2;
    if (filename.toLowerCase().includes('copy')) score -= 15;
    if (
      filename.toLowerCase().includes('(1)') ||
      filename.toLowerCase().includes('(2)')
    )
      score -= 15;
    if (filename.toLowerCase().includes('duplicate')) score -= 20;
    if (isLiveVersion(filename) || isLiveVersion(track.tags?.title || ''))
      score -= 12;
    if (isExplicitVersion(filename)) score -= 3;

    return { index: idx, score, extension: fileExtension };
  });
};
